import { Reservation } from '../models/reservation';
import { ReservationStatusCode } from '../models/reservation-status-code.enum';
import { OnyxEvent } from '../models/event';
import { ReservationCompare } from '../models/reservation-compare';
import { ComparatorUtils } from './comparator-utils';
import { isNullOrUndefined } from 'util';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { NumberUtils } from '../../shared/utils/number.util';

export class ReservationUtils {

  /**
   * Do not include reservation value when the reservation no longer has possible value.
   *
   * If the record has a status of No Show or Cancelled exclude reservations from the calculations.
   *
   * @param reservation
   * @returns {boolean}
   */
  static hasReservationValue(reservation: Reservation): boolean {
    let value = reservation.status;

    if (value) {
      value = value.toUpperCase();
    }

    let returnValue = false;

    switch (value) {
      case ReservationStatusCode.CANCELLED.toString():
        returnValue = false;
        break;
      case ReservationStatusCode.NO_SHOW.toString():
        returnValue = false;
        break;
      default:
        returnValue = true;
    }

    return returnValue;
  }

  static getCommissionRate(onyxEvent: OnyxEvent): number {
    if (onyxEvent) {
      return onyxEvent.commissionRoomPercentage;
    } else {
      return 0;
    }

  }

  static getRevenue(reservation: Reservation): number {
    if (this.hasReservationValue(reservation)) {
      return reservation.nights * reservation.roomRate;
    } else {
      return 0;
    }
  }

  static getCommission(reservation: Reservation, onyxEvent: OnyxEvent): number {
    if (!this.hasReservationValue(reservation)) {
      return 0;
    }

    let revenue = reservation.revenue;

    if (!revenue) {
      revenue = this.getRevenue(reservation);
    }

    return (revenue * this.getCommissionRate(onyxEvent)) / 100;
  }

  static reservationCompareComparator(obj1: ReservationCompare, obj2: ReservationCompare): number {
    let compareValue = ComparatorUtils.compareNullable(obj1, obj2);
    if (compareValue !== 0 || (isNullOrUndefined(obj1) && isNullOrUndefined(obj2))) {
      return compareValue;
    }

    compareValue = ComparatorUtils.compareNullable(obj1.reservation, obj2.reservation);
    if (compareValue !== 0 || (isNullOrUndefined(obj1.reservation) && isNullOrUndefined(obj2.reservation))) {
      return compareValue;
    }

    return ComparatorUtils.compareLastNameFirstName(
      { firstName: obj1.reservation.firstName, lastName: obj1.reservation.lastName },
      { firstName: obj2.reservation.firstName, lastName: obj2.reservation.lastName });
  }

  static calculateRevenueTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.revenue))
        .map(compare => compare.reservation.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateCommissionTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.commission))
        .map(compare => compare.reservation.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateRsvpCommissionTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.commission))
        .map(compare => compare.rsvp.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateReservationTotalNights(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.nights))
        .map(compare => compare.reservation.nights)
        .reduce((prev: number, current: number) => prev + current, 0);
    });
  }

  static isListEmpty(obsReservationList: Observable<List<ReservationCompare>>): Observable<boolean> {
    return ReservationUtils.getListSize(obsReservationList).map(compareListSize => compareListSize === 0);
  }

  static getListSize(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map(compareList => compareList.size);
  }

  static onlyReservationsFilter(reservationList: List<ReservationCompare>): List<ReservationCompare> {
    return reservationList.filter(compare => compare.reservation ? true : false).toList();
  }

  static onlyRsvpFilter(reservationList: List<ReservationCompare>): List<ReservationCompare> {
    return reservationList.filter(compare => compare.rsvp ? true : false).toList();
  }

  static getNumberOfRsvps(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return (compareList ? this.onlyRsvpFilter(compareList).size : 0);
    });
  }

  static calculateCommission(revenue: number, commissionPercentage: number): number {
    return revenue * commissionPercentage / 100;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/utils/reservation-utils.ts